import {
  LocalStorageKeys,
  MOBILE_SCREEN_SIZE,
  TABLET_SCREEN_SIZE,
} from 'app-constants';
import { useGetApplicationByReferenceSuspense } from 'hooks/use-get-application-by-reference-suspense';
import { useGetCurrentApplicationSuspense } from 'hooks/use-get-current-application-suspense';
import type { RootLogic } from 'models';
import { useEffect } from 'react';

import { useCheckUpdateClientUserLanguage } from '../../use-check-update-client-user-language';
import { useGetApplicationPrivateInfo } from '../../use-get-application-private-info';
import { useGetCurrentStepCount } from '../../use-get-current-step-count';
import { useGetLanguages } from '../../use-get-languages';
import { useGetPageUrl } from '../../use-get-page-url';
import { useGetProductTypeByUrl } from '../../use-get-product-type-by-url';
import { useGetUserSuspense } from '../../use-get-user-suspence';
import { useInitializeAppServices } from '../../use-initialize-app-services';
import { useTrackGoogleAnalytics } from '../../use-track-google-analytics';
import { useCheckMaxResolution, useLocalStorage } from '../../utils';

export const useRootLogic = (): RootLogic => {
  const productType = useGetProductTypeByUrl();
  const { languages } = useGetLanguages();
  const checkUpdateClientUserLanguage = useCheckUpdateClientUserLanguage();

  const { user, getUser, userLoading, userWasFetched, quietUserRefetch } =
    useGetUserSuspense();

  // will be skipped automatically inside of hook if ref key is missed
  const { application } = (
    user
      ? useGetCurrentApplicationSuspense
      : useGetApplicationByReferenceSuspense
  )();

  const {
    applicationPrivateInfo,
    applicationPrivateInfoLoading,
    getApplicationPrivateInfo,
  } = useGetApplicationPrivateInfo(application.schedule_type);
  const currentStepCount = useGetCurrentStepCount();
  const isTablet = useCheckMaxResolution(TABLET_SCREEN_SIZE);
  const isMobile = useCheckMaxResolution(MOBILE_SCREEN_SIZE);
  const { getPageUrlAndNavigate, pageUrlAndNavigationProcessing, pageUrl } =
    useGetPageUrl();
  const trackGoogleAnalyticsEvent = useTrackGoogleAnalytics();
  const [initApplicationSchedule, setInitApplicationSchedule] = useLocalStorage<
    Record<number, string>
  >(LocalStorageKeys.initialApplicationSchedule, {});
  useInitializeAppServices(user);

  useEffect(() => {
    if (application.id && !initApplicationSchedule[application.id]) {
      setInitApplicationSchedule((prev) => ({
        ...prev,
        [application.id]: application.schedule_type,
      }));
    }
  }, [
    application.id,
    initApplicationSchedule,
    application.schedule_type,
    setInitApplicationSchedule,
  ]);

  useEffect(() => {
    if (userWasFetched) {
      checkUpdateClientUserLanguage();
    }
  }, [userWasFetched, checkUpdateClientUserLanguage]);

  return {
    pageUrl,
    productType,
    applicationPrivateInfo,
    applicationPrivateInfoLoading,
    application,
    languages,
    user,
    currentStepCount,
    isTablet,
    isMobile,
    getUser,
    quietUserRefetch,
    userWasFetched,
    userLoading,
    getPageUrlAndNavigate,
    pageUrlAndNavigationProcessing,
    getApplicationPrivateInfo,
    trackGoogleAnalyticsEvent,
  };
};
